import omit from 'lodash/omit';
import set from 'lodash/set';

import {selectCurrentJourneyId} from '@/models/journey';
import {
  changeCustomerNotesRequest,
  fetchCustomerDetailsRequest,
  fetchCustomersForJourney,
  fetchCustomerStatesRequest,
  unSubscribeCustomerRequest,
} from '@/services/api/customer';
import {getChangeLoadings, setChangeLoadingsUtility} from '@/utils/form';
import {processAPIResponse} from '@/utils/request';
import {trackModalView, trackOnlineEditForm} from '@/services/tracker';
import {CUSTOMER_EDIT_ACTION} from '@/constants/trackerActions';
import {selectDateFilter} from '@/models/date';
import {openCSVLink} from '@/utils/download';
import {CustomerModel} from '@/typings/models/Customer';
import {parseSort} from '@/utils/functions';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import {FILTER_PLACES} from '@/components/Filters/LeadFilters';
import {adaptListResponse} from '@/utils/listParams';

const initialState = {
  list: {},
  listParams: {},
  states: [],
  details: {},
  changeLoadings: {},
  drawer: {
    customer: {},
    visible: false
  }
};

export default {
  namespace: 'customers',

  state: initialState,

  effects: {
    * fetch({payload = {}}: {payload: {search?: string, url?: string, concat?: boolean}}, {call, put, select}) {
      const date = yield select(selectDateFilter)
      const journeyId = yield select(selectCurrentJourneyId)
      const filters = yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.CUSTOMERS_LIST))
      const storedParams = yield select((state: any) => state.customers.listParams)

      // 'search' in payload → explicit value (including empty clear); absent → keep stored
      const search = 'search' in payload ? payload.search : storedParams?.search
      if (search !== storedParams?.search) {
        yield put({ type: 'savedListParams', payload: { search } })
      }

      const query: Record<string, any> = {...date};
      if (search) query.search = search;
      if (filters?.sort) {
        const { sort_by, sort_direction } = parseSort(filters.sort);
        if (sort_by && sort_direction) {
          query.sort_by = sort_by;
          query.sort_direction = sort_direction;
        }
      }
      const {response, data} = yield call(fetchCustomersForJourney, {params: {journeyId}, url: payload.url, query});
      yield processAPIResponse({response, data}, {
        * onSuccess(data) {
          yield put({
            type: 'saveCustomers',
            payload: {
              ...adaptListResponse(data),
              concat: payload.concat,
            }
          })
        },
        * onError(error){

        }
      })

    },
    * downloadExport({ payload = {} }: {payload: {url?: string}}, { call, put, select }) {
      const date = yield select(selectDateFilter)
      const query = {...date, export: true};
      const journeyId = yield select(selectCurrentJourneyId)
      const {response, data} = yield call(fetchCustomersForJourney, {params: {journeyId}, url: payload.url, query});
      yield processAPIResponse({response, data}, {
        // The export link lives under `data.data.links`, which is what the
        // 'data' dataKey below hands to onSuccess — reading the raw wrapper
        // instead finds nothing and the download silently no-ops.
        * onSuccess(customers) {
          if (customers?.links?.export) {
            yield openCSVLink(customers.links.export)
          }
        },
        * onError(error){

        }
      }, 'data')

    },
    * fetchStates({}, {call, put, select}) {
      const journeyId = yield select(selectCurrentJourneyId)
      const {response, data} = yield call(fetchCustomerStatesRequest, {params: {journeyId}});

      yield processAPIResponse({response, data}, {
        * onSuccess(data) {
          yield put({
            type: 'saveCustomerStates',
            payload: data
          })
        },
        * onError(error){

        }
      })
    },
    * fetchDetails({payload: {customerId}}, {call, put, select}) {
      const {response, data} = yield call(fetchCustomerDetailsRequest, {
          params: {customerId},
        }
      );
      yield processAPIResponse({response, data}, {
        * onSuccess(data) {
          yield put({
            type: 'saveCustomer',
            payload: {
              customerId,
              customer: data.Customer
            }
          })
        },
        * onError(error){
          // if (error) message.error(error.message)
          yield put({
            type: "fetchCustomerDetailsFailed"
          })
        }
      }, 'data')
    },
    * change({payload: {customerId, ...body}}, {call, put}) {
    yield put({
      type: 'editCustomerLocally',
      payload: {customerId, ...body}
    })
      yield put({
        type: "setChangeLoadings",
        payload: getChangeLoadings(body, true)
      })
      const {response, data} = yield call(changeCustomerNotesRequest, {
          params: {customerId},
          payload: body
        }
      );
      yield put({
        type: "setChangeLoadings",
        payload: getChangeLoadings(body, false)
      })
      trackOnlineEditForm(body, CUSTOMER_EDIT_ACTION)

      yield processAPIResponse({response, data}, {
        * onSuccess(data) {},
        * onError(error){
        }
      }, 'data')
    },

    * unSubscribe({payload: {customerId}}, {call, put}) {
      const {response, data} = yield call(unSubscribeCustomerRequest, {
          params: {customerId},
          payload: {
            subscribe: false
          }
        }
      );
      if (response.status > 300) {
        return;
      }
      yield processAPIResponse({response, data}, {
        * onSuccess(data) {
          yield put({
            type: 'fetch'
          })
          yield put({
            type: 'fetchDetails',
            payload: {
              customerId
            }
          })

        },
        * onError(error){
        }
      })

    }
  },
  reducers: {
    editCustomerLocally(state, action) {
      const {customerId} = action.payload
      const mergedCustomer = (customer: CustomerModel) => {
        const toOverride = {...action.payload, ...(action.payload.leadOverride || {})}
        Object.keys(omit(toOverride, ['customerId'])).forEach(key => {
          set(customer?.Profile, key, toOverride[key])
        })
        return customer
      }

      const toUpdate: any = {}

      const prevLead = state.details[customerId]
      if (prevLead) {
        const newCustomer = mergedCustomer(prevLead)
        toUpdate.details = {
          ...state.details,
          [customerId]: {
            ...newCustomer

          }
        }
      }

      const prevLeadDetails = state.details[customerId]
      if (prevLeadDetails) {
        const newCustomerDetails = mergedCustomer(prevLeadDetails)
        toUpdate.details = {
          ...state.details,
          [customerId]: {
            ...newCustomerDetails

          }
        }
      }

      return {
        ...state,
        ...toUpdate
      }
    },
    saveCustomers(state, action) {
      const { concat, ...listData } = action.payload;
      return {
        ...state,
        list: concat
          ? { ...listData, items: [...(state.list.items || []), ...listData.items] }
          : listData,
      };
    },
    savedListParams(state, action) {
      return { ...state, listParams: { ...state.listParams, ...action.payload } };
    },
    saveCustomer(state, action) {
      return {
        ...state,
        details: {
          ...state.details,
          [action.payload.customerId]: action.payload.customer
        },
      };
    },
    saveCustomerStates(state, action) {
      return {
        ...state,
        states: action.payload.data,
      };
    },
    setChangeLoadings(state, action) {
      return setChangeLoadingsUtility(state, action)
    },
    fetchCustomerDetailsFailed(state, action) {
      return {
        ...state,
        drawer: {
          visible: false
        }
      }
    },
    toggleDrawer(state, action) {
      const {payload: {customer, nested}} = action;
      const drawer = state.drawer || {};
      drawer.nested = nested
      if (customer) {
        drawer.customer = customer
        drawer.visible = true
        trackModalView('customer-details')
      } else {
        drawer.visible = false
      }

      return {
        ...state,
        drawer
      }
    },
    reset() {
      return initialState
    }
  }
};
